import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [status, setStatus] = useState('verifying'); // verifying | ready | submitting | error
  const [errorMsg, setErrorMsg] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Enlace inválido. Falta el token.');
      return;
    }
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({ detail: 'Enlace inválido' }));
          throw new Error(err.detail || 'Enlace inválido');
        }
        const data = await resp.json();
        setUserInfo(data);
        setStatus('ready');
      } catch (e) {
        setStatus('error');
        setErrorMsg(e.message);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (password !== confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setStatus('submitting');
    try {
      const resp = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Error al restablecer la contraseña' }));
        throw new Error(err.detail || 'Error al restablecer la contraseña');
      }
      const data = await resp.json();
      useAuthStore.setState({
        user: data.user,
        token: data.token,
        isAuthenticated: true,
        viewAsRole: data.user.role,
      });
      toast.success('¡Contraseña actualizada! Has iniciado sesión.');
      const roleDest = {
        ceo: '/ceo-dashboard',
        manager: '/dashboard',
        member: '/member-dashboard',
        client: '/client-portal',
      };
      navigate(roleDest[data.user.role] || '/dashboard', { replace: true });
    } catch (e) {
      setStatus('ready');
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-100 p-4">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center mx-auto mb-3 text-2xl font-bold">K</div>
            <CardTitle className="text-2xl">Restablecer contraseña</CardTitle>
            <CardDescription>Crea una nueva contraseña para tu cuenta</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'verifying' && (
              <div className="flex flex-col items-center py-8 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                <p className="text-sm text-muted-foreground">Verificando tu enlace...</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="reset-error">
                <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-900">Enlace no válido</p>
                  <p className="text-sm text-red-700 mt-1">{errorMsg}</p>
                  <Button variant="outline" className="mt-3" onClick={() => navigate('/login')} data-testid="reset-go-login-btn">
                    Ir al inicio de sesión
                  </Button>
                </div>
              </div>
            )}

            {(status === 'ready' || status === 'submitting') && userInfo && (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <div className="text-sm">
                    <span className="text-emerald-900">Hola </span>
                    <span className="font-semibold text-emerald-900">{userInfo.name}</span>
                    <span className="text-emerald-700"> ({userInfo.email})</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Nueva contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      required
                      minLength={8}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="reset-toggle-pwd"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirma la contraseña</Label>
                  <Input
                    id="confirm"
                    type={showPwd ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repítela"
                    required
                    minLength={8}
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full gradient-primary text-white"
                  disabled={status === 'submitting'}
                  data-testid="reset-submit-btn"
                >
                  {status === 'submitting' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                  ) : (
                    'Guardar nueva contraseña'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
